import moment, { Moment } from "moment";
import { v5 as uuidV5 } from "uuid";

import { EDITING_REQUEST_PLACEHOLDER_CLASS_NAME, doRangesOverlap, getCurrentReportingValues, shouldShowNextYearCropsFeatureFlag } from "utils";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CropFCPO,
  CropItemData,
  EDITING_REQUEST_TYPE,
  EditingRequestPlaceholderYears,
  FieldPracticeResponse,
  GetVerificationEditingRequestsResponse,
  PRACTICE_BUNDLE_SLUG,
  VerificationEditingRequest,
} from "models";

import { GROUP_TYPE, PRACTICE_TYPE, TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";
import { getCropGroupType, getCropPracticeType } from "pages/app/field-timeline/utils/CropUtils.ts";

const DEFAULT_BASELINE_YEARS = 3;
const MAX_FUTURE_YEARS = 5;
const PLACEHOLDER_NAMESPACE = "86a8e6f9-48dc-490f-96b8-1b89c5ca511c";

const createDeterministicUUID = ({ group, practiceType, startDate }: { group: string | GROUP_TYPE; startDate: string; practiceType: PRACTICE_TYPE }) => {
  const deterministicUUIDValue = `${startDate}_${practiceType}_${group}`;
  return uuidV5(deterministicUUIDValue, PLACEHOLDER_NAMESPACE);
};

// check the carbon runner response for the crops baseline years, if the field is not enrolled or is an inventory farm, we default to the 3 years before the current year
const getFieldBaselineYears = (carbonRunnerBaselineYears: number[], currentYear: number) =>
  carbonRunnerBaselineYears.length > 0
    ? carbonRunnerBaselineYears
    : Array(DEFAULT_BASELINE_YEARS)
        .fill(0)
        .map((_, index) => currentYear - (index + 1));

const getFieldCropEntryTargetYear = ({
  type,
  seedingStartDate,
  harvestEndDate,
  carbonProgram,
}: {
  type: CROP_TYPE_FCPO;
  seedingStartDate: Moment;
  harvestEndDate: Moment;
  carbonProgram: CarbonProgram | null;
}) => {
  if (carbonProgram?.accountingMethodology === "inventory") {
    const end = carbonProgram.reportingPeriod?.end;
    const harvestDateYear = harvestEndDate.year();
    return harvestEndDate.dayOfYear() > moment(`${end}/${harvestDateYear}`).dayOfYear() ? harvestDateYear + 1 : harvestDateYear;
  }

  if (type === CROP_TYPE_FCPO.MAIN) return harvestEndDate.year();
  if (type === CROP_TYPE_FCPO.COVER || type === CROP_TYPE_FCPO.UNDERSOWING) return seedingStartDate.year();
  return undefined;
};

const createCropItem = ({
  start,
  end,
  isBaseline,
  isMandatory,
  cropType,
  carbonProgram,
  groupId,
  isFlaggedByVerifier,
}: {
  start: Moment;
  end: Moment;
  isBaseline: boolean;
  isMandatory: boolean;
  isFlaggedByVerifier?: boolean;
  cropType: CROP_TYPE_FCPO;
  carbonProgram: CarbonProgram | null;
  groupId?: string;
}): TimelineCalendarItemsProps<CropItemData> => {
  const group = groupId ?? getCropGroupType(cropType);
  const practiceType = getCropPracticeType(cropType);
  return {
    id: createDeterministicUUID({ group, practiceType, startDate: start.toISOString() }),
    start_time: start,
    end_time: end,
    group: group,
    canResize: false,
    canMove: false,
    isPlaceholder: true,
    practiceType: practiceType,
    className: `placeholder-crop ${
      cropType === CROP_TYPE_FCPO.MAIN
        ? "main-crop"
        : cropType === CROP_TYPE_FCPO.COVER
        ? "cover-crop"
        : cropType === CROP_TYPE_FCPO.UNDERSOWING
        ? "undersowing-crop"
        : ""
    }${isFlaggedByVerifier ? ` ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}` : ""}`,
    data: {
      cropName: "",
      year: getFieldCropEntryTargetYear({ type: cropType, seedingStartDate: start, harvestEndDate: end, carbonProgram }),
      hasResidue: false,
      isBaseline: isBaseline,
      isMandatory: isMandatory,
      isConfirmed: false,
      cropType: cropType,
      payout: undefined,
      fertilizerManagements: [],
      yieldTonsPerHa: null,
      fieldId: groupId,
      isFlaggedByVerifier,
    },
  };
};

const filterDuplicates = (placeholders: TimelineCalendarItemsProps<CropItemData>[]) =>
  placeholders.filter((value, index, array) => {
    const overlappingPlaceholder = array.find((item) => {
      if (item.id === value.id) return false;
      return doRangesOverlap(value.start_time, value.end_time, item.start_time, item.end_time);
    });
    console.assert(!overlappingPlaceholder, "An overlapping placeholder has been removed, but there should be no overlapping placeholders!"); // eslint-disable-line
    return !overlappingPlaceholder;
  });

const getFutureYearsPlaceholders = (
  maxFutureYears: number,
  lastHarvestDate: Moment | undefined,
  cropType: CROP_TYPE_FCPO,
  baselineYears: number[],
  isFieldEnrolled: boolean,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  carbonProgram: CarbonProgram | null,
  groupId?: string
): TimelineCalendarItemsProps<CropItemData>[] => {
  const placeholders: TimelineCalendarItemsProps<CropItemData>[] = [];
  const { activeFarmReportingYear, getReportingYearRange, endDate } = reportingValues;
  const latestPossibleFutureYear = activeFarmReportingYear + maxFutureYears;
  const lastFutureCropDate = lastHarvestDate ?? endDate;
  const yearDifference = latestPossibleFutureYear - moment(lastFutureCropDate).year();
  if (yearDifference > 0) {
    Array(yearDifference)
      .fill(0)
      .forEach((_, index) => {
        const futureYear = moment(lastFutureCropDate).year() + index + 1;
        const { start, end } = getReportingYearRange(futureYear);
        const isBaseline = isFieldEnrolled && baselineYears.includes(futureYear);
        const cropItem = createCropItem({
          start,
          end,
          isBaseline,
          isMandatory: false,
          cropType,
          carbonProgram,
          groupId,
        });
        placeholders.push(cropItem);
      });
  }

  return placeholders;
};

const getEnrollmentFlags = (allPractices: FieldPracticeResponse) => {
  const activeBundleSlugs = new Set(allPractices.availablePracticeBundles.filter((bundle) => bundle.active).map((bundle) => bundle.slug));

  return {
    hasCropsEnrollment: activeBundleSlugs.has(PRACTICE_BUNDLE_SLUG.CROPS),
    hasFertilizerOrTillageEnrollment: activeBundleSlugs.has(PRACTICE_BUNDLE_SLUG.FERTILIZER) || activeBundleSlugs.has(PRACTICE_BUNDLE_SLUG.TILLAGE),
  };
};

const getMainCropFutureYearsPlaceholders = (
  carbonProgram: CarbonProgram | null,
  maxFutureYears: number,
  allPractices: FieldPracticeResponse,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  groupId?: string
): TimelineCalendarItemsProps<CropItemData>[] => {
  const { activeFarmReportingYear, getReportingYearRange } = reportingValues;
  if (!activeFarmReportingYear || !maxFutureYears) return [];
  const mainCrops = allPractices.crops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN);

  const yearsWithMainCrop = Array.from(new Set(mainCrops.map((crop) => moment(crop.harvestEndDate).year())));
  // reporting year + future years
  const placeholders: TimelineCalendarItemsProps<CropItemData>[] = Array(maxFutureYears + 1)
    .fill(0)
    .map((_, i) => activeFarmReportingYear + i)
    .reduce((acc, year) => {
      if (!yearsWithMainCrop.includes(year)) {
        const { end: yearEnd } = getReportingYearRange(year);
        // needs to be stringified, otherwise moment will treat it as a UNIX timestamp
        const start = moment(year.toString()).startOf("year");
        const overlappingMainCrop = mainCrops.find((crop) => doRangesOverlap(start, yearEnd, moment(crop.seedingStartDate), moment(crop.harvestEndDate)));
        const mainCropPlaceholderEnd = overlappingMainCrop ? moment(overlappingMainCrop.seedingStartDate).clone().subtract(1, "day") : yearEnd;

        const diffInWeeks = mainCropPlaceholderEnd.diff(start, "weeks");
        if (diffInWeeks < 4) return acc;

        const shouldShowPlaceholdersForThisYear =
          shouldShowNextYearCropsFeatureFlag() && (year === activeFarmReportingYear || year === activeFarmReportingYear + 1);

        const { hasCropsEnrollment, hasFertilizerOrTillageEnrollment } = getEnrollmentFlags(allPractices);

        const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

        const hasRequiredEnrollment = isInventoryProgram
          ? true
          : (year === activeFarmReportingYear && hasCropsEnrollment) ||
            (year === activeFarmReportingYear + 1 && hasCropsEnrollment && hasFertilizerOrTillageEnrollment);

        const isMandatory = hasRequiredEnrollment && shouldShowPlaceholdersForThisYear;

        const cropItem = createCropItem({
          start,
          end: mainCropPlaceholderEnd,
          isBaseline: false,
          isMandatory,
          cropType: CROP_TYPE_FCPO.MAIN,
          carbonProgram,
          groupId,
        });
        acc.push(cropItem);
      }
      return acc;
    }, [] as TimelineCalendarItemsProps<CropItemData>[]);

  return placeholders;
};

const getBaselinePlaceholders = (
  baselineYears: number[],
  lastConfirmationDate: string | undefined,
  mainCrops: CropFCPO[],
  isFieldEnrolled: boolean,
  carbonProgram: CarbonProgram | null,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  groupId?: string
): TimelineCalendarItemsProps<CropItemData>[] => {
  const placeholders: TimelineCalendarItemsProps<CropItemData>[] = baselineYears.reduce((acc, baselineYear) => {
    if (lastConfirmationDate) {
      if (baselineYear <= moment(lastConfirmationDate).year()) {
        return acc;
      }
    }
    const baselineHasMainCrop = mainCrops.find((mainCrop) => moment(mainCrop.harvestEndDate).year() === baselineYear);
    if (!baselineHasMainCrop) {
      const { start: yearStart, end: yearEnd } = reportingValues.getReportingYearRange(baselineYear);

      const overlappingMainCrop = mainCrops.find((crop) => doRangesOverlap(yearStart, yearEnd, moment(crop.seedingStartDate), moment(crop.harvestEndDate)));
      const mainCropPlaceholderEnd = overlappingMainCrop ? moment(overlappingMainCrop.seedingStartDate).clone().subtract(1, "day") : yearEnd;

      const diffInWeeks = mainCropPlaceholderEnd.diff(yearStart, "weeks");
      if (diffInWeeks < 4) return acc;

      const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

      const isMandatory = isFieldEnrolled && !isInventoryProgram;
      const cropItem = createCropItem({
        start: yearStart,
        end: mainCropPlaceholderEnd,
        isBaseline: isFieldEnrolled,
        isMandatory,
        cropType: CROP_TYPE_FCPO.MAIN,
        carbonProgram,
        groupId,
      });
      acc.push(cropItem);
    }
    return acc;
  }, [] as TimelineCalendarItemsProps<CropItemData>[]);

  return placeholders;
};

const handleGaps = (
  prevCrop: CropFCPO,
  currentValue: CropFCPO,
  placeholders: TimelineCalendarItemsProps<CropItemData>[],
  baselineYears: number[],
  mainCrops: CropFCPO[],
  cropType: CROP_TYPE_FCPO,
  isFieldEnrolled: boolean,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  carbonProgram: CarbonProgram | null,
  groupId?: string
): CropFCPO => {
  if (!prevCrop.harvestEndDate) return currentValue;
  const diffInWeeks = moment(currentValue.seedingStartDate).diff(prevCrop.harvestEndDate, "weeks");
  if (diffInWeeks < 4) return currentValue;

  let start = moment(prevCrop.harvestEndDate).add(1, "day");
  const end = moment(currentValue.seedingStartDate).subtract(1, "day");
  const placeholderExists = placeholders.find((placeholder) => doRangesOverlap(start, end, placeholder.start_time, placeholder.end_time));
  if (placeholderExists) {
    // Use the remaining space, if more than 4 weeks to fill
    start = moment(placeholderExists.end_time).add(1, "day");
    const diffInWeeks = end.diff(start, "weeks");
    if (diffInWeeks < 4) return currentValue;
  }
  // Determine the reporting year this gap falls into
  const gapYear = end.year();
  const isBaseline = isFieldEnrolled && baselineYears.includes(gapYear);

  // Check if there's already a crop for this reporting year
  const cropForThatYearExists = mainCrops.find((crop) => {
    const cropYear = moment(crop.harvestEndDate).year();
    return cropYear === gapYear;
  });

  // A crop is mandatory if:
  // 1. The field is enrolled AND
  // 2. It's a main crop AND
  // 3. Either it's a baseline year OR it's the current reporting year AND
  // 4. There is no existing crop for that year
  const isMandatory =
    isFieldEnrolled && cropType === CROP_TYPE_FCPO.MAIN && (isBaseline || gapYear === reportingValues.activeFarmReportingYear) && !cropForThatYearExists;
  const cropItem = createCropItem({
    start,
    end,
    isBaseline,
    isMandatory,
    cropType,
    carbonProgram,
    groupId,
  });
  placeholders.push(cropItem);
  return currentValue;
};

const getPlaceholdersForEmptyCrops = (
  cropType: CROP_TYPE_FCPO,
  isFieldEnrolled: boolean,
  carbonProgram: CarbonProgram | null,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  allPractices: FieldPracticeResponse,
  baselineYears: number[],
  groupId?: string,
  editingRequestYears?: number[]
): TimelineCalendarItemsProps<CropItemData>[] => {
  const bauYearPlaceholders = baselineYears.map((year) => {
    const { start, end } = reportingValues.getReportingYearRange(year);
    const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

    const isFlaggedByVerifier = !!editingRequestYears?.includes(year);

    const isMandatory = isFlaggedByVerifier || (cropType === CROP_TYPE_FCPO.MAIN && isFieldEnrolled && !isInventoryProgram);
    const cropItem = createCropItem({
      start,
      end,
      isBaseline: isFieldEnrolled,
      isMandatory,
      cropType,
      carbonProgram,
      groupId,
      isFlaggedByVerifier,
    });

    if (isFlaggedByVerifier) {
      cropItem.className = `${cropItem.className} ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}`;
    }
    return cropItem;
  });

  const firstYearAfterBaseline = Math.max(...baselineYears) + 1;

  // 1 year after baseline (crops practice enrollment year) + 5 future years
  const currentAndFutureYearPlaceholders = Array(MAX_FUTURE_YEARS + 1)
    .fill(null)
    .map((_, index) => {
      const futureYear = firstYearAfterBaseline + index;
      const { start, end } = reportingValues.getReportingYearRange(futureYear);

      const { hasCropsEnrollment, hasFertilizerOrTillageEnrollment } = getEnrollmentFlags(allPractices);
      const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

      const isFlaggedByVerifier = !!editingRequestYears?.includes(futureYear);

      const isMandatory =
        isFlaggedByVerifier ||
        (isInventoryProgram ? index < 2 : (index === 0 && hasCropsEnrollment) || (index === 1 && hasCropsEnrollment && hasFertilizerOrTillageEnrollment));

      const cropItem = createCropItem({
        start,
        end,
        isBaseline: false,
        isMandatory,
        cropType,
        carbonProgram,
        groupId,
        isFlaggedByVerifier,
      });

      if (isFlaggedByVerifier) {
        cropItem.className = `${cropItem.className} ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}`;
        cropItem.data.isFlaggedByVerifier = true;
      }
      return cropItem;
    });

  return [...bauYearPlaceholders, ...currentAndFutureYearPlaceholders];
};

export const createPlaceholderForMainCrops = (
  allPractices: FieldPracticeResponse,
  baselineYears: number[],
  isFieldEnrolled: boolean,
  carbonProgram: CarbonProgram | null,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  groupId?: string,
  editingRequestPlaceholderYears?: EditingRequestPlaceholderYears
): TimelineCalendarItemsProps<CropItemData>[] => {
  const allCrops = allPractices.crops;
  const mainCrops = allCrops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN);
  const fieldBaselineYears = getFieldBaselineYears(baselineYears, reportingValues.activeFarmReportingYear);

  const editingRequestPlaceholderMainCropsYears = editingRequestPlaceholderYears?.[CROP_TYPE_FCPO.MAIN] ?? [];

  // Crops not existing
  if (!allCrops.length) {
    return getPlaceholdersForEmptyCrops(
      CROP_TYPE_FCPO.MAIN,
      isFieldEnrolled,
      carbonProgram,
      reportingValues,
      allPractices,
      fieldBaselineYears,
      groupId,
      editingRequestPlaceholderMainCropsYears
    );
  }

  const placeholders: TimelineCalendarItemsProps<CropItemData>[] = [];

  const cropsSortedByHarvestYearDESC = allCrops.sort((a, b) => moment(b.harvestEndDate).valueOf() - moment(a.harvestEndDate).valueOf());
  const lastCrop = cropsSortedByHarvestYearDESC?.[0];
  if (!lastCrop) {
    console.assert(lastCrop, "There should be at least one crop with a harvest date!"); // eslint-disable-line
    return placeholders;
  }

  // Add future year placeholders
  const futureYearPlaceholders = getMainCropFutureYearsPlaceholders(carbonProgram, MAX_FUTURE_YEARS, allPractices, reportingValues, groupId);
  placeholders.push(...futureYearPlaceholders);

  // Fill placeholders gaps
  // | 2024             |      2023                    | 2024               | 2025 (Current)  | 2026              |
  // | Main 🔒         |             Main 🔒          |             Main   |(Placeholder ✅) | (Placeholder ✅)  |
  // |          (Place | holder ❌)            (Place | holder ✅)        |(Placeholder ✅) | (Placeholder ✅)  |

  const confirmedCrops: CropFCPO[] = allCrops.filter((crop) => crop.isConfirmed);
  const lastConfirmedCrop = confirmedCrops.sort((a, b) => moment(b.harvestEndDate).valueOf() - moment(a.harvestEndDate).valueOf())?.[0];
  const lastConfirmationDate = lastConfirmedCrop?.harvestEndDate;

  // Add baseline placeholders
  const baselinePlaceholders = getBaselinePlaceholders(
    fieldBaselineYears,
    lastConfirmationDate,
    mainCrops,
    isFieldEnrolled,
    carbonProgram,
    reportingValues,
    groupId
  );

  placeholders.push(...baselinePlaceholders);

  const uniquePlaceholders = filterDuplicates(placeholders).sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf());

  editingRequestPlaceholderMainCropsYears.forEach((requestYear) => {
    const hasCropForYear = mainCrops.some((crop) => moment(crop.year).year() === requestYear);

    if (!hasCropForYear) {
      const existingPlaceholderIndex = uniquePlaceholders.findIndex((p) => p.data.year === requestYear);

      if (existingPlaceholderIndex >= 0) {
        // update existing placeholder into a mandatory placeholder
        const existingPlaceholder = uniquePlaceholders[existingPlaceholderIndex];
        existingPlaceholder.data.isMandatory = true;
        existingPlaceholder.className = `${existingPlaceholder.className} ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}`;
        existingPlaceholder.data.isFlaggedByVerifier = true;
      } else {
        // Create new mandatory placeholder for thsi year
        const { start, end } = reportingValues.getReportingYearRange(requestYear);

        const overlappingMainCrop = mainCrops.find((crop) => doRangesOverlap(start, end, moment(crop.seedingStartDate), moment(crop.harvestEndDate)));

        const mainCropPlaceholderEnd = overlappingMainCrop ? moment(overlappingMainCrop.seedingStartDate).clone().subtract(1, "day") : end;

        const diffInWeeks = mainCropPlaceholderEnd.diff(start, "weeks");

        if (diffInWeeks >= 4) {
          const cropItem = createCropItem({
            start,
            end: mainCropPlaceholderEnd,
            isBaseline: fieldBaselineYears.includes(requestYear),
            isMandatory: true,
            cropType: CROP_TYPE_FCPO.MAIN,
            carbonProgram,
            groupId,
            isFlaggedByVerifier: true,
          });

          cropItem.className = `${cropItem.className} ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}`;
          cropItem.data.isFlaggedByVerifier = true;
          uniquePlaceholders.push(cropItem);
        }
      }
    }
  });

  // Resorting placeholders after adding editing request placeholders —— since we used push, I just think it makes sense; maybe not haha 🙈
  uniquePlaceholders.sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf());

  const mainCropYears = mainCrops.map((crop) => moment(crop.harvestEndDate).year());
  const mainCropPlaceholderYears = uniquePlaceholders.map((crop) => moment(crop.end_time).year());
  const futureYears = Array(MAX_FUTURE_YEARS + 1)
    .fill(null)
    .map((_, i) => moment().year() + i);
  const missingMainCropPlaceholderYears = [...fieldBaselineYears, ...futureYears].filter(
    (year) => ![...mainCropYears, ...mainCropPlaceholderYears].includes(year)
  );

  if (missingMainCropPlaceholderYears.length === 0) return uniquePlaceholders;

  missingMainCropPlaceholderYears.forEach((year) => {
    const prevMainCrop = mainCrops.find((crop) => moment(crop.harvestEndDate).year() === year - 1);
    const nextMainCrop = mainCrops.find((crop) => moment(crop.harvestEndDate).year() === year + 1);
    if (!prevMainCrop || !nextMainCrop) return;

    const diffInWeeks = moment(nextMainCrop.seedingStartDate).diff(prevMainCrop.harvestEndDate, "weeks");
    if (diffInWeeks < 4) return;

    handleGaps(
      prevMainCrop,
      nextMainCrop,
      uniquePlaceholders,
      fieldBaselineYears,
      mainCrops,
      CROP_TYPE_FCPO.MAIN,
      isFieldEnrolled,
      reportingValues,
      carbonProgram,
      groupId
    );
  });

  return uniquePlaceholders;
};

export const createPlaceholderForUndersowingCrops = (
  allCrops: CropFCPO[],
  baselineYears: number[],
  isFieldEnrolled: boolean,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  carbonProgram: CarbonProgram | null,
  groupId?: string,
  editingRequestUndersowingCropPlaceholderYears?: EditingRequestPlaceholderYears
): TimelineCalendarItemsProps<CropItemData>[] => {
  const placeholders: TimelineCalendarItemsProps<CropItemData>[] = [];

  const mainCrops = allCrops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN);
  const undersowingCrops = allCrops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.UNDERSOWING);

  const editingRequestUndersowingCropYears = editingRequestUndersowingCropPlaceholderYears?.[CROP_TYPE_FCPO.UNDERSOWING] ?? [];

  const lastConfirmedCrop = mainCrops
    .filter((crop) => crop.isConfirmed)
    .sort((a, b) => moment(b.harvestEndDate).valueOf() - moment(a.harvestEndDate).valueOf())?.[0];
  const { activeFarmReportingYear, getReportingYearRange } = reportingValues;

  mainCrops.forEach((mainCrop) => {
    const { start: reportingYearStart, end: reportingYearEnd } = getReportingYearRange(activeFarmReportingYear);
    const isHarvestInCurrentReportingYear = moment(mainCrop.harvestEndDate).isBetween(reportingYearStart, reportingYearEnd, "day", "[]");

    // just skipping confirmed crops UNLESS their harvest falls in the current reporting year
    if (mainCrop.isConfirmed && !isHarvestInCurrentReportingYear) return;

    const hasUnderSowing = undersowingCrops.find((undersowingCrop) =>
      doRangesOverlap(
        moment(mainCrop.seedingStartDate),
        moment(mainCrop.harvestEndDate),
        moment(undersowingCrop.seedingStartDate),
        moment(undersowingCrop.harvestEndDate)
      )
    );
    if (hasUnderSowing) return;
    if (lastConfirmedCrop && !isHarvestInCurrentReportingYear) {
      if (lastConfirmedCrop.harvestEndDate) {
        const mainCropIsInLastConfirmedYear = moment(mainCrop.seedingStartDate).year() <= moment(lastConfirmedCrop.harvestEndDate).year();
        if (mainCropIsInLastConfirmedYear) return;
      }
    }

    const isBaseline = isFieldEnrolled && !!mainCrop.year && baselineYears.includes(mainCrop.year);

    const isFlaggedByVerifier = !!(mainCrop.year && editingRequestUndersowingCropYears.includes(mainCrop.year));

    const undersowingStartTime =
      isHarvestInCurrentReportingYear && moment(mainCrop.seedingStartDate).year() < activeFarmReportingYear
        ? reportingYearStart.clone()
        : moment(mainCrop.seedingStartDate);

    const group = groupId ?? GROUP_TYPE.SIDE_CROP;
    const cropItem: TimelineCalendarItemsProps<CropItemData> = {
      id: createDeterministicUUID({ group, practiceType: PRACTICE_TYPE.UNDERSOWING_CROP, startDate: undersowingStartTime.toISOString() }),
      start_time: undersowingStartTime,
      end_time: moment(mainCrop.harvestEndDate),
      group: group,
      canResize: false,
      canMove: false,
      isPlaceholder: true,
      practiceType: PRACTICE_TYPE.UNDERSOWING_CROP,
      className: `placeholder-crop undersowing-crop${isFlaggedByVerifier ? ` ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}` : ""}`,
      data: {
        cropName: "",
        year: getFieldCropEntryTargetYear({
          type: CROP_TYPE_FCPO.UNDERSOWING,
          seedingStartDate: moment(mainCrop.seedingStartDate),
          harvestEndDate: moment(mainCrop.harvestEndDate),
          carbonProgram,
        }),
        hasResidue: false,
        isBaseline: isBaseline,
        isMandatory: isFlaggedByVerifier,
        isConfirmed: false,
        cropType: CROP_TYPE_FCPO.UNDERSOWING,
        payout: undefined,
        fertilizerManagements: [],
        yieldTonsPerHa: null,
        isFlaggedByVerifier: isFlaggedByVerifier,
      },
    };
    placeholders.push(cropItem);
  });

  return placeholders.sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf());
};

const createPlaceholderForCoverCrops = (
  allPractices: FieldPracticeResponse,
  baselineYears: number[],
  isFieldEnrolled: boolean,
  carbonProgram: CarbonProgram | null,
  reportingValues: ReturnType<typeof getCurrentReportingValues>,
  groupId?: string,
  editingRequestCoverCropPlaceholderYears?: EditingRequestPlaceholderYears
): TimelineCalendarItemsProps<CropItemData>[] => {
  const placeholders: TimelineCalendarItemsProps<CropItemData>[] = [];
  const { crops: allCrops } = allPractices;
  const coverCrops = allCrops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.COVER);
  const fieldBaselineYears = getFieldBaselineYears(baselineYears, reportingValues.activeFarmReportingYear);

  const editingRequestCoverCropYears = editingRequestCoverCropPlaceholderYears?.[CROP_TYPE_FCPO.COVER] ?? [];

  // There are no Crops
  if (!allCrops.length) {
    const emptyCropsPlaceholders = getPlaceholdersForEmptyCrops(
      CROP_TYPE_FCPO.COVER,
      isFieldEnrolled,
      carbonProgram,
      reportingValues,
      allPractices,
      fieldBaselineYears,
      groupId,
      editingRequestCoverCropYears
    );
    placeholders.push(...emptyCropsPlaceholders);
    return placeholders;
  }

  // No confirmed
  // Find last crop
  const cropsSortedByHarvestYearDESC = allPractices.crops.sort((a, b) => moment(b.harvestEndDate).valueOf() - moment(a.harvestEndDate).valueOf());
  const lastCrop = cropsSortedByHarvestYearDESC?.[0];
  if (!lastCrop) {
    console.assert(lastCrop, "There should be at least one crop with a harvest date!"); // eslint-disable-line
    return placeholders;
  }

  // Add future year placeholders
  const futureYearPlaceholders = getFutureYearsPlaceholders(
    MAX_FUTURE_YEARS,
    moment(lastCrop.harvestEndDate),
    CROP_TYPE_FCPO.COVER,
    fieldBaselineYears,
    isFieldEnrolled,
    reportingValues,
    carbonProgram,
    groupId
  );
  placeholders.push(...futureYearPlaceholders);

  // Fill placeholders gaps
  // | 2024             |      2023                    | 2024               | 2025 (Current)  | 2026              |
  // | Main 🔒         |             Main 🔒          |             Main   |(Placeholder ✅) | (Placeholder ✅)  |
  // |          (Place | holder ❌)            (Place | holder ✅)        |(Placeholder ✅) | (Placeholder ✅)  |

  const confirmedCrops: CropFCPO[] = allCrops.filter((crop) => crop.isConfirmed);
  const lastConfirmedCrop = confirmedCrops.sort((a, b) => moment(b.harvestEndDate).valueOf() - moment(a.harvestEndDate).valueOf())?.[0];
  const lastConfirmationDate = lastConfirmedCrop?.harvestEndDate;

  if (!lastConfirmationDate) {
    // Fill the time from the first crop beginning year to the start of the first crop
    const firstCrop = allCrops[allCrops.length - 1];
    const firstCropYear = moment(firstCrop.seedingStartDate).year();
    const { start: startOfFirstYear } = reportingValues.getReportingYearRange(firstCropYear);

    const timeFromBeginningOfLastYearUntilFirstCrop = moment(firstCrop.seedingStartDate).diff(startOfFirstYear, "weeks");
    if (timeFromBeginningOfLastYearUntilFirstCrop >= 4) {
      const start = startOfFirstYear;
      const end = moment(firstCrop.seedingStartDate).subtract(1, "day");
      const isBaseline = isFieldEnrolled && baselineYears.includes(firstCropYear);
      const cropItem = createCropItem({
        start,
        end,
        isBaseline,
        isMandatory: false,
        cropType: CROP_TYPE_FCPO.COVER,
        carbonProgram,
        groupId,
      });
      placeholders.push(cropItem);
    }

    // Fill the time from the last crop end date until the end of that year
    const lastCropYear = moment(lastCrop.harvestEndDate).year();
    const { end: endOfLastYear } = reportingValues.getReportingYearRange(lastCropYear);

    const timeFromLastCropEndDateUntilTheEndOfThatYear = moment(endOfLastYear).diff(lastCrop.harvestEndDate, "weeks");
    if (timeFromLastCropEndDateUntilTheEndOfThatYear >= 4) {
      const start = moment(lastCrop.harvestEndDate).add(1, "day");
      const end = endOfLastYear;
      const isBaseline = isFieldEnrolled && baselineYears.includes(lastCropYear);
      const cropItem = createCropItem({
        start,
        end,
        isBaseline,
        isMandatory: false,
        cropType: CROP_TYPE_FCPO.COVER,
        carbonProgram,
        groupId,
      });
      placeholders.push(cropItem);
    }

    allCrops
      .sort((a, b) => moment(a.harvestEndDate).valueOf() - moment(b.harvestEndDate).valueOf())
      .reduce(
        (prevCrop, currentValue) =>
          handleGaps(
            prevCrop,
            currentValue,
            placeholders,
            baselineYears,
            coverCrops,
            CROP_TYPE_FCPO.COVER,
            isFieldEnrolled,
            reportingValues,
            carbonProgram,
            groupId
          ),
        {} as CropFCPO
      );
  }

  if (lastConfirmationDate) {
    // Fill the time from the last crop end date until the end of that year

    const lastCropYear = moment(lastCrop.harvestEndDate).year();
    const { end: endOfLastYear } = reportingValues.getReportingYearRange(lastCropYear);

    const timeFromLastCropEndDateUntilTheEndOfThatYear = moment(endOfLastYear).diff(moment(lastCrop.harvestEndDate), "weeks");
    if (timeFromLastCropEndDateUntilTheEndOfThatYear >= 4) {
      const start = moment(lastCrop.harvestEndDate).add(1, "day");
      const end = endOfLastYear;
      const cropItem = createCropItem({
        start,
        end,
        isBaseline: false,
        isMandatory: false,
        cropType: CROP_TYPE_FCPO.COVER,
        carbonProgram,
        groupId,
      });
      placeholders.push(cropItem);
    }
    const cropsAfterLastConfirmation = allCrops
      .filter((crop) => {
        const isAfterLastConfirmationDate = moment(crop.harvestEndDate).isAfter(lastConfirmationDate);
        return isAfterLastConfirmationDate;
      })
      .sort((a, b) => moment(a.harvestEndDate).valueOf() - moment(b.harvestEndDate).valueOf());

    if (cropsAfterLastConfirmation.length) {
      cropsAfterLastConfirmation.reduce(
        (prevCrop, currentValue) =>
          handleGaps(
            prevCrop,
            currentValue,
            placeholders,
            baselineYears,
            coverCrops,
            CROP_TYPE_FCPO.COVER,
            isFieldEnrolled,
            reportingValues,
            carbonProgram,
            groupId
          ),
        {} as CropFCPO
      );
    }
  }

  const finalPlaceholders = filterDuplicates(placeholders);

  editingRequestCoverCropYears.forEach((requestYear) => {
    const hasCoverCropForYear = coverCrops.some((crop) => moment(crop.seedingStartDate).year() === requestYear);

    if (!hasCoverCropForYear) {
      const existingPlaceholderIndex = finalPlaceholders.findIndex((p) => p.data.year === requestYear);

      if (existingPlaceholderIndex >= 0) {
        const existingPlaceholder = finalPlaceholders[existingPlaceholderIndex];
        existingPlaceholder.data.isMandatory = true;
        existingPlaceholder.className = `${existingPlaceholder.className} ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}`;
        existingPlaceholder.data.isFlaggedByVerifier = true;
      } else {
        const { start, end } = reportingValues.getReportingYearRange(requestYear);

        const cropItem = createCropItem({
          start,
          end,
          isBaseline: fieldBaselineYears.includes(requestYear),
          isMandatory: true,
          cropType: CROP_TYPE_FCPO.COVER,
          carbonProgram,
          groupId,
          isFlaggedByVerifier: true,
        });

        cropItem.className = `${cropItem.className} ${EDITING_REQUEST_PLACEHOLDER_CLASS_NAME}`;
        cropItem.data.isFlaggedByVerifier = true;
        finalPlaceholders.push(cropItem);
      }
    }
  });

  return finalPlaceholders.sort((a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf());
};

export const getEditingRequestPlaceholderYears = (fieldId: string, editingRequests: GetVerificationEditingRequestsResponse): EditingRequestPlaceholderYears => {
  const filteredRequests = editingRequests.reduce(
    (acc, r) => {
      if (
        r.fieldId === fieldId &&
        r.completedAt === null &&
        r.requestType === EDITING_REQUEST_TYPE.CROP &&
        r.fieldCropId === null &&
        r.completedAt === null &&
        r.cropType !== null
      ) {
        return { ...acc, [r.cropType]: [...acc[r.cropType], r.year] };
      }
      return acc;
    },
    { [CROP_TYPE_FCPO.MAIN]: [], [CROP_TYPE_FCPO.COVER]: [], [CROP_TYPE_FCPO.UNDERSOWING]: [] }
  );

  return filteredRequests;
};

export const createPlaceholderItems = (
  fieldPracticeResponse: FieldPracticeResponse,
  baselineYears: number[],
  isFieldEnrolled: boolean,
  carbonProgram: CarbonProgram | null,
  reportingYear: number,
  groupId?: string,
  verificationEditingRequests?: VerificationEditingRequest[]
): TimelineCalendarItemsProps<CropItemData>[] => {
  console.assert(fieldPracticeResponse.crops !== undefined, "We need to have fieldPracticeResponse.crops "); // eslint-disable-line

  const reportingValues = getCurrentReportingValues(reportingYear, carbonProgram);

  const editingRequestPlaceholderYears = getEditingRequestPlaceholderYears(fieldPracticeResponse.fieldId, verificationEditingRequests ?? []);

  const mainCropPlaceholders = createPlaceholderForMainCrops(
    fieldPracticeResponse,
    baselineYears,
    isFieldEnrolled,
    carbonProgram,
    reportingValues,
    groupId,
    editingRequestPlaceholderYears
  );
  const coverCropPlaceholders = createPlaceholderForCoverCrops(
    fieldPracticeResponse,
    baselineYears,
    isFieldEnrolled,
    carbonProgram,
    reportingValues,
    groupId,
    editingRequestPlaceholderYears
  );
  const underSowingCropPlaceholders = createPlaceholderForUndersowingCrops(
    fieldPracticeResponse.crops,
    baselineYears,
    isFieldEnrolled,
    reportingValues,
    carbonProgram,
    groupId,
    editingRequestPlaceholderYears
  );

  return [...mainCropPlaceholders, ...underSowingCropPlaceholders, ...coverCropPlaceholders];
};
